import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { GoogleAuthDebug } from '../components/Auth/GoogleAuthDebug';
import { GoogleSignInButton } from '../components/Auth/GoogleSignInButton';
import { useAuth } from '../contexts/AuthContext';
/**
 * Google Auth Test Page
 * Use this page to test Google OAuth integration
 * Add to AppRouter.tsx: <Route path="/test-google" element={<GoogleAuthTestPage />} />
 */
export function GoogleAuthTestPage() {
  const {
    user,
    signInWithGoogle,
    signOut
  } = useAuth();
  const [loading, setLoading] = useState(false);
  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Sign-in error:', error);
      setLoading(false);
    }
  };
  const handleSignOut = async () => {
    await signOut();
  };
  return <div className="flex flex-col w-full min-h-screen bg-[#f8f6f2]" data-id="element-4013">
      <header className="bg-white border-b border-gray-200" data-id="element-4014">
        <div className="max-w-4xl mx-auto px-5 py-4" data-id="element-4015">
          <Link to="/" className="inline-flex items-center text-gray-600 hover:text-purple-600 text-sm" data-id="element-4016">
            <ArrowLeft size={16} className="mr-1" data-id="element-4017" />
            Back to Home
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-5 py-12" data-id="element-4018">
        <div className="w-full max-w-2xl" data-id="element-4019">
          <div className="bg-white rounded-xl shadow-sm p-8 mb-6" data-id="element-4020">
            <h1 className="text-3xl font-bold mb-2" data-id="element-4021">
              🧪 Google OAuth Test Page
            </h1>
            <p className="text-gray-600 mb-6" data-id="element-4022">
              Use this page to test your Google Sign-In integration
            </p>

            {/* Current Status */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6" data-id="element-4023">
              <h2 className="font-semibold mb-3" data-id="element-4024">Current Status</h2>
              <div className="space-y-2 text-sm" data-id="element-4025">
                <div className="flex items-center justify-between" data-id="element-4026">
                  <span data-id="element-4027">Authentication:</span>
                  <span className={`font-medium ${user ? 'text-green-600' : 'text-red-600'}`} data-id="element-4028">
                    {user ? '✅ Logged In' : '❌ Not Logged In'}
                  </span>
                </div>
                {user && <>
                    <div className="flex items-center justify-between" data-id="element-4029">
                      <span data-id="element-4030">Email:</span>
                      <span className="font-medium" data-id="element-4031">{user.email}</span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-4032">
                      <span data-id="element-4033">Name:</span>
                      <span className="font-medium" data-id="element-4034">
                        {user.user_metadata?.fullName || 'Not set'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-4035">
                      <span data-id="element-4036">Provider:</span>
                      <span className="font-medium" data-id="element-4037">
                        {user.app_metadata?.provider || 'Unknown'}
                      </span>
                    </div>
                  </>}
              </div>
            </div>

            {/* Test Actions */}
            <div className="space-y-4" data-id="element-4038">
              {!user ? <>
                  <h2 className="font-semibold mb-3" data-id="element-4039">Test Google Sign-In</h2>
                  <GoogleSignInButton onClick={handleGoogleSignIn} loading={loading} text="Test Google Sign-In" data-id="element-4040" />
                  <p className="text-xs text-gray-500 text-center" data-id="element-4041">
                    Click the button above to test the Google OAuth flow
                  </p>
                </> : <>
                  <h2 className="font-semibold mb-3" data-id="element-4042">
                    Logged In Successfully!
                  </h2>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4" data-id="element-4043">
                    <p className="text-sm text-green-800" data-id="element-4044">
                      ✅ Google Sign-In is working correctly!
                    </p>
                  </div>
                  <button onClick={handleSignOut} className="w-full bg-red-600 text-white py-3 rounded-lg font-medium hover:bg-red-700 transition-colors" data-id="element-4045">
                    Sign Out
                  </button>
                </>}
            </div>

            {/* Setup Checklist */}
            <div className="mt-8 pt-6 border-t border-gray-200" data-id="element-4046">
              <h2 className="font-semibold mb-3" data-id="element-4047">Setup Checklist</h2>
              <div className="space-y-2 text-sm" data-id="element-4048">
                <label className="flex items-start" data-id="element-4049">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-4050" />
                  <span data-id="element-4051">Enabled Google provider in Supabase Dashboard</span>
                </label>
                <label className="flex items-start" data-id="element-4052">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-4053" />
                  <span data-id="element-4054">Added OAuth Client ID and Secret in Supabase</span>
                </label>
                <label className="flex items-start" data-id="element-4055">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-4056" />
                  <span data-id="element-4057">Verified redirect URL in Google Cloud Console</span>
                </label>
                <label className="flex items-start" data-id="element-4058">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-4059" />
                  <span data-id="element-4060">Disabled "Skip nonce checks" in Supabase</span>
                </label>
                <label className="flex items-start" data-id="element-4061">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-4062" />
                  <span data-id="element-4063">Disabled "Allow users without email" in Supabase</span>
                </label>
              </div>
            </div>

            {/* Quick Links */}
            <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-4064">
              <h2 className="font-semibold mb-3" data-id="element-4065">Quick Links</h2>
              <div className="space-y-2" data-id="element-4066">
                <a href="https://supabase.com/dashboard/project/neadfoxscgjeekvabnoz/auth/providers" target="_blank" rel="noopener noreferrer" className="block text-sm text-blue-600 hover:underline" data-id="element-4067">
                  → Supabase Auth Providers Dashboard
                </a>
                <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="block text-sm text-blue-600 hover:underline" data-id="element-4068">
                  → Google Cloud Console Credentials
                </a>
                <Link to="/signup" className="block text-sm text-purple-600 hover:underline" data-id="element-4069">
                  → Go to Signup Page
                </Link>
                <Link to="/login" className="block text-sm text-purple-600 hover:underline" data-id="element-4070">
                  → Go to Login Page
                </Link>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6" data-id="element-4071">
            <h3 className="font-semibold text-blue-900 mb-3" data-id="element-4072">
              📋 Testing Instructions
            </h3>
            <ol className="space-y-2 text-sm text-blue-800" data-id="element-4073">
              <li data-id="element-4074">1. Complete the setup checklist above</li>
              <li data-id="element-4075">2. Click "Test Google Sign-In" button</li>
              <li data-id="element-4076">3. You'll be redirected to Google OAuth</li>
              <li data-id="element-4077">4. Authorize the app</li>
              <li data-id="element-4078">5. You'll be redirected back (to /onboarding)</li>
              <li data-id="element-4079">6. Check that you're logged in</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Debug Panel */}
      <GoogleAuthDebug data-id="element-4080" />
    </div>;
}